/* ==========================================================================
   Michael Vardani — custom product page behaviour
   Self-contained custom elements. Every listener is bound in
   connectedCallback and torn down in disconnectedCallback, so Shopify theme
   editor section reloads never leave duplicate handlers behind.
   No globals are created other than the custom element definitions.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------- helpers --- */

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function defaultTo(number, precision, thousands, decimal) {
      precision = typeof precision === 'number' ? precision : 2;
      thousands = typeof thousands === 'string' ? thousands : ',';
      decimal = typeof decimal === 'string' ? decimal : '.';
      if (isNaN(number) || number === null) return '0';
      var fixed = (Number(number) / 100).toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = defaultTo(cents, 2);
        break;
      case 'amount_no_decimals':
        value = defaultTo(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = defaultTo(cents, 2, '.', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = defaultTo(cents, 2, ' ', '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = defaultTo(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = defaultTo(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = defaultTo(cents, 2, "'");
        break;
      default:
        value = defaultTo(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  function safeParse(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[custom-product] could not parse embedded JSON', e);
      return null;
    }
  }

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --------------------------------------------------------- gallery --- */

  function MediaGallery(root) {
    this.root = root;
    this.stage = root.querySelector('[data-mv-stage]');
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-mv-media]'));
    this.thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-mv-thumb]'));
  }

  MediaGallery.prototype.select = function (mediaId, focusThumb) {
    var found = false;
    this.items.forEach(function (item) {
      var match = String(item.dataset.mvMedia) === String(mediaId);
      if (match) found = true;
      item.toggleAttribute('data-active', match);
      item.setAttribute('aria-hidden', match ? 'false' : 'true');
      if (!match) {
        var video = item.querySelector('video');
        if (video && !video.paused) video.pause();
      }
      // Media that was never shown keeps lazy images; reveal on first select.
      if (match) {
        var lazy = item.querySelectorAll('img[loading="lazy"][data-mv-defer]');
        Array.prototype.forEach.call(lazy, function (img) {
          img.removeAttribute('data-mv-defer');
          img.setAttribute('loading', 'eager');
        });
      }
    });
    if (!found) return;
    this.thumbs.forEach(function (thumb) {
      var active = String(thumb.dataset.mvThumb) === String(mediaId);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      thumb.setAttribute('tabindex', active ? '0' : '-1');
      if (active && focusThumb) thumb.focus();
      if (active && thumb.scrollIntoView) {
        try {
          thumb.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
          });
        } catch (e) {
          thumb.scrollIntoView(false);
        }
      }
    });
    this.current = mediaId;
  };

  MediaGallery.prototype.step = function (delta) {
    if (!this.thumbs.length) return;
    var index = this.thumbs.findIndex(
      function (t) {
        return t.getAttribute('aria-current') === 'true';
      }
    );
    if (index < 0) index = 0;
    var next = (index + delta + this.thumbs.length) % this.thumbs.length;
    this.select(this.thumbs[next].dataset.mvThumb, true);
  };

  /* ------------------------------------------------------- mv-product --- */

  if (!customElements.get('mv-product')) {
    class MvProduct extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.data = safeParse(this.querySelector('[data-mv-product-json]')) || {};
        this.variants = this.data.variants || [];
        this.moneyFormat = this.data.money_format || '${{amount}}';
        this.strings = this.data.strings || {};

        this.form = this.querySelector('form.mv-pdp__form');
        this.idInput = this.querySelector('[data-mv-variant-id]');
        this.qtyInput = this.querySelector('[data-mv-qty-input]');
        this.submit = this.querySelector('[data-mv-submit]');
        this.submitLabel = this.querySelector('[data-mv-submit-label]');
        this.gallery = new MediaGallery(this);

        this.onOptionChange = this.onOptionChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onQtyInput = this.onQtyInput.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);

        this.addEventListener('change', this.onOptionChange);
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        if (this.qtyInput) this.qtyInput.addEventListener('input', this.onQtyInput);
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        this.currentVariant = this.findVariantFromSelection() || this.variants[0] || null;
        this.updateUI({ initial: true });
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onOptionChange);
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        if (this.qtyInput) this.qtyInput.removeEventListener('input', this.onQtyInput);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.initialised = false;
      }

      /* -------------------------------------------------- selections -- */

      get selectedOptions() {
        var picker = this.querySelector('[data-mv-options]');
        if (!picker) return [];
        return Array.prototype.map.call(
          picker.querySelectorAll('[data-mv-option-group]'),
          function (group) {
            var select = group.querySelector('select[data-mv-option-input]');
            if (select) return select.value;
            var checked = group.querySelector('input:checked');
            return checked ? checked.value : null;
          }
        );
      }

      findVariantFromSelection() {
        var selected = this.selectedOptions;
        if (!selected.length) return this.variants[0] || null;
        return (
          this.variants.filter(function (variant) {
            return selected.every(function (value, index) {
              return value === null || variant.options[index] === value;
            });
          })[0] || null
        );
      }

      onOptionChange(event) {
        var target = event.target;
        if (!target) return;
        if (target.matches('[data-mv-option-input], [data-mv-variant-select]')) {
          this.currentVariant = this.findVariantFromSelection();
          this.updateUI({});
        }
        if (target.matches('[data-mv-selling-plan]')) {
          this.updateUI({});
        }
      }

      onQtyInput() {
        this.updatePriceAndButton();
      }

      onKeydown(event) {
        var thumb = event.target.closest && event.target.closest('[data-mv-thumb]');
        if (thumb && (event.key === 'ArrowRight' || event.key === 'ArrowDown')) {
          event.preventDefault();
          this.gallery.step(1);
        } else if (thumb && (event.key === 'ArrowLeft' || event.key === 'ArrowUp')) {
          event.preventDefault();
          this.gallery.step(-1);
        }
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        var details = event.target.querySelector('details');
        if (details) details.open = true;
      }

      onClick(event) {
        var el = event.target.closest
          ? event.target.closest('[data-mv-thumb],[data-mv-qty-step],[data-mv-wish],[data-mv-zoom],[data-mv-lightbox-close]')
          : null;
        if (!el) return;

        if (el.hasAttribute('data-mv-thumb')) {
          event.preventDefault();
          this.gallery.select(el.dataset.mvThumb, false);
          return;
        }

        if (el.hasAttribute('data-mv-qty-step')) {
          event.preventDefault();
          this.stepQuantity(parseInt(el.dataset.mvQtyStep, 10) || 0);
          return;
        }

        if (el.hasAttribute('data-mv-zoom')) {
          event.preventDefault();
          this.openLightbox();
          return;
        }

        if (el.hasAttribute('data-mv-lightbox-close')) {
          event.preventDefault();
          this.closeLightbox();
        }
      }

      /* --------------------------------------------------- quantity --- */

      stepQuantity(delta) {
        if (!this.qtyInput) return;
        var step = parseInt(this.qtyInput.step, 10) || 1;
        var min = parseInt(this.qtyInput.min, 10) || 1;
        var max = this.qtyInput.max ? parseInt(this.qtyInput.max, 10) : null;
        var value = (parseInt(this.qtyInput.value, 10) || min) + delta * step;
        if (value < min) value = min;
        if (max !== null && value > max) value = max;
        this.qtyInput.value = value;
        this.updatePriceAndButton();
      }

      get quantity() {
        if (!this.qtyInput) return 1;
        return Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
      }

      /* ---------------------------------------------------- lightbox --- */

      openLightbox() {
        var dialog = this.querySelector('[data-mv-lightbox]');
        if (!dialog) return;
        var active = this.querySelector('[data-mv-media][data-active] img');
        var target = dialog.querySelector('img');
        if (active && target) {
          target.src = active.currentSrc || active.src;
          target.alt = active.alt || '';
          if (active.srcset) target.srcset = active.srcset;
        }
        this.lightboxOpener = document.activeElement;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        var close = dialog.querySelector('[data-mv-lightbox-close]');
        if (close) close.focus();
      }

      closeLightbox() {
        var dialog = this.querySelector('[data-mv-lightbox]');
        if (!dialog) return;
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
        if (this.lightboxOpener && this.lightboxOpener.focus) this.lightboxOpener.focus();
      }

      /* ------------------------------------------------------ update --- */

      updateUI(options) {
        var initial = options && options.initial;
        this.updateAvailability();
        this.updatePriceAndButton();
        this.updateMeta();
        if (!initial) {
          this.updateMedia();
          this.updateUrl();
          this.notify();
        }
      }

      updateAvailability() {
        var self = this;
        var selected = this.selectedOptions;
        var groups = this.querySelectorAll('[data-mv-option-group]');
        Array.prototype.forEach.call(groups, function (group) {
          var index = parseInt(group.dataset.mvOptionGroup, 10);
          var select = group.querySelector('select[data-mv-option-input]');
          var candidates = select
            ? Array.prototype.slice.call(select.options)
            : Array.prototype.slice.call(group.querySelectorAll('input[data-mv-option-input]'));

          candidates.forEach(function (node) {
            var trial = selected.slice();
            trial[index] = node.value;
            var matches = self.variants.filter(function (variant) {
              return trial.every(function (value, i) {
                return value === null || variant.options[i] === value;
              });
            });
            var available = matches.some(function (variant) {
              return variant.available;
            });
            if (select) {
              node.classList.toggle('mv-pdp__option--unavailable', !available);
            } else {
              var wrapper = node.closest('.mv-pdp__pill');
              if (wrapper) wrapper.classList.toggle('mv-pdp__pill--unavailable', !available);
            }
          });
          var label = group.querySelector('[data-mv-option-current]');
          if (label && selected[index]) label.textContent = selected[index];
        });
      }

      get sellingPlanId() {
        var checked = this.querySelector('[data-mv-selling-plan]:checked');
        return checked && checked.value ? parseInt(checked.value, 10) : null;
      }

      currentPricing() {
        var variant = this.currentVariant;
        if (!variant) return null;
        var planId = this.sellingPlanId;
        if (planId && variant.selling_plan_allocations) {
          var allocation = variant.selling_plan_allocations.filter(function (a) {
            return a.selling_plan_id === planId;
          })[0];
          if (allocation) {
            return {
              price: allocation.price,
              compare_at_price: allocation.compare_at_price || variant.price,
            };
          }
        }
        return { price: variant.price, compare_at_price: variant.compare_at_price };
      }

      updatePriceAndButton() {
        var variant = this.currentVariant;
        var priceEl = this.querySelector('[data-mv-price]');
        var pricing = this.currentPricing();

        if (priceEl && pricing) {
          var current = priceEl.querySelector('[data-mv-price-current]');
          var compare = priceEl.querySelector('[data-mv-price-compare]');
          if (current) current.textContent = formatMoney(pricing.price, this.moneyFormat);
          if (compare) {
            compare.textContent = pricing.compare_at_price
              ? formatMoney(pricing.compare_at_price, this.moneyFormat)
              : '';
          }
          var onSale = !!(pricing.compare_at_price && pricing.compare_at_price > pricing.price);
          priceEl.setAttribute('data-on-sale', onSale ? 'true' : 'false');
          priceEl.setAttribute('data-sold-out', variant && variant.available ? 'false' : 'true');
        }

        var installments = this.querySelector('[data-mv-installments]');
        if (installments && pricing) {
          var count = parseInt(installments.dataset.mvInstallments, 10) || 4;
          var each = Math.round(pricing.price / count);
          var template = installments.dataset.mvInstallmentsTemplate || '';
          installments.textContent = template
            .replace('[count]', String(count))
            .replace('[amount]', formatMoney(each, this.moneyFormat));
          installments.hidden = !variant;
        }

        if (this.idInput) {
          this.idInput.value = variant ? variant.id : '';
          this.idInput.disabled = !variant || !variant.available;
        }

        if (this.qtyInput && variant && variant.quantity_rule) {
          this.qtyInput.min = variant.quantity_rule.min || 1;
          this.qtyInput.step = variant.quantity_rule.increment || 1;
          if (variant.quantity_rule.max) this.qtyInput.max = variant.quantity_rule.max;
          else this.qtyInput.removeAttribute('max');
          if (parseInt(this.qtyInput.value, 10) < (variant.quantity_rule.min || 1)) {
            this.qtyInput.value = variant.quantity_rule.min || 1;
          }
        }

        if (this.submit) {
          var unavailable = !variant;
          var soldOut = variant && !variant.available;
          this.submit.disabled = unavailable || soldOut;
          if (this.submitLabel) {
            var label;
            if (unavailable) label = this.strings.unavailable || 'Unavailable';
            else if (soldOut) label = this.strings.sold_out || 'Sold out';
            else {
              label = this.submit.dataset.mvLabel || this.strings.add_to_cart || 'Add to cart';
              if (this.submit.dataset.mvShowTotal === 'true' && pricing) {
                label += ' — ' + formatMoney(pricing.price * this.quantity, this.moneyFormat);
              }
            }
            this.submitLabel.textContent = label;
          }
        }

        var stickyLabel = this.querySelector('[data-mv-sticky-label]');
        if (stickyLabel && this.submitLabel) stickyLabel.textContent = this.submitLabel.textContent;
      }

      updateMeta() {
        var variant = this.currentVariant;

        var sku = this.querySelector('[data-mv-sku]');
        if (sku) {
          var value = variant && variant.sku ? variant.sku : '';
          var skuValue = sku.querySelector('[data-mv-sku-value]');
          if (skuValue) skuValue.textContent = value;
          sku.hidden = !value;
        }

        var stock = this.querySelector('[data-mv-stock]');
        if (stock && stock.dataset.mvStockDynamic === 'true') {
          var text = stock.dataset.mvStockDefault || '';
          var modifier = '';
          if (variant) {
            var threshold = parseInt(stock.dataset.mvStockThreshold, 10) || 0;
            if (!variant.available) {
              text = stock.dataset.mvStockOut || text;
              modifier = 'out';
            } else if (
              variant.inventory_management &&
              variant.inventory_quantity !== null &&
              variant.inventory_quantity > 0 &&
              variant.inventory_quantity <= threshold
            ) {
              text = (stock.dataset.mvStockLow || '').replace('[count]', variant.inventory_quantity);
              modifier = 'low';
            }
          }
          stock.textContent = text;
          stock.classList.toggle('mv-pdp__stock--low', modifier === 'low');
          stock.classList.toggle('mv-pdp__stock--out', modifier === 'out');
        }
      }

      updateMedia() {
        var variant = this.currentVariant;
        if (!variant || !variant.featured_media_id) return;
        this.gallery.select(variant.featured_media_id, false);
      }

      updateUrl() {
        if (!this.currentVariant || !this.data.url) return;
        if (this.dataset.updateUrl === 'false') return;
        if (window.history && window.history.replaceState) {
          var url = new URL(this.data.url, window.location.origin);
          url.searchParams.set('variant', this.currentVariant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      notify() {
        this.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: { variant: this.currentVariant, sectionId: this.dataset.sectionId },
          })
        );
      }
    }

    customElements.define('mv-product', MvProduct);
  }

  /* --------------------------------------------- sticky mobile bar --- */

  if (!customElements.get('mv-sticky-atc')) {
    class MvStickyAtc extends HTMLElement {
      connectedCallback() {
        this.anchor = document.getElementById(this.dataset.anchor || '');
        if (!this.anchor || !('IntersectionObserver' in window)) {
          this.hidden = true;
          return;
        }
        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(
              function (entry) {
                this.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
              }.bind(this)
            );
          }.bind(this),
          { rootMargin: '0px 0px -80% 0px' }
        );
        this.observer.observe(this.anchor);
        this.addEventListener('click', this.onClick.bind(this));
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      onClick(event) {
        if (!event.target.closest('[data-mv-sticky-submit]')) return;
        event.preventDefault();
        var button = document.querySelector('[data-mv-submit]');
        if (button) button.click();
      }
    }

    customElements.define('mv-sticky-atc', MvStickyAtc);
  }
})();
